import { Plan } from '@wix/ambassador-checkout-server/types';
import _ from 'lodash';
import {
  PricingPlan,
  ReservedLocationIds,
  Service,
  StaffMember,
} from '@wix/bookings-uou-types';
import { CalendarState } from '../../components/BookingCalendar/controller';
import { CalendarStatus } from '../../components/BookingCalendar/ViewModel/widgetViewModel/widgetViewModel';
import { FilterTypes } from '../../components/BookingCalendar/ViewModel/filterViewModel/filterViewModel';
import {
  BookingsQueryParams,
  WixOOISDKAdapter,
} from '@wix/bookings-adapter-ooi-wix-sdk';
import { EmptyStateType } from '../../components/BookingCalendar/ViewModel/emptyStateViewModel/emptyStateViewModel';
import { ServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { Optional, Preset, SlotsStatus } from '../../types/types';
import { getValidPurchasedPricingPlansForService } from '../pricingPlans/pricingPlans';
import { Balance } from '@wix/ambassador-pricing-plan-benefits-server/types';
import { Booking } from '@wix/bookings-checkout-api';
import { extractQueryParamValues } from '../queryParams/queryParams';
import { Experiments, IWixAPI } from '@wix/yoshi-flow-editor';
import { DO_NOT_CARE_STAFF } from '../../constants/constants';
import { getUrlQueryParamValue } from '@wix/bookings-catalog-calendar-viewer-utils';
import type { CalendarSelections } from '@wix/bookings-catalog-calendar-viewer-utils';

export function createInitialState({
  wixCodeApi,
  servicesInView,
  allCalendarBookableServices,
  wixSdkAdapter,
  staffMembers,
  rescheduleBookingDetails,
  initialErrors,
  allPurchasedPricingPlans,
  isPricingPlanInstalled,
  isUserLoggedIn,
  servicesVariants,
  preset,
  experiments,
  calendarSelections,
}: {
  servicesInView: Service[];
  allCalendarBookableServices?: Service[];
  wixSdkAdapter: WixOOISDKAdapter;
  wixCodeApi: IWixAPI;
  staffMembers?: StaffMember[];
  rescheduleBookingDetails?: Booking;
  initialErrors: EmptyStateType[];
  allPurchasedPricingPlans?: Balance[];
  isPricingPlanInstalled: boolean;
  isUserLoggedIn: boolean;
  servicesVariants?: ServiceOptionsAndVariants[];
  preset: Preset;
  experiments?: Experiments;
  calendarSelections?: CalendarSelections;
}): CalendarState {
  const locationFilterOptions = initializeLocationFilterOptions({
    wixCodeApi,
    wixSdkAdapter,
    calendarSelections,
    experiments,
  });
  const staffFilterOptions = initializeStaffMembersFilterOptions({
    wixCodeApi,
    staffMembers,
    wixSdkAdapter,
    preset,
    servicesInView,
    experiments,
    calendarSelections,
  });
  const purchasedPricingPlans = initializePurchasedPricingPlans({
    servicesInView,
    allPurchasedPricingPlans,
    isPricingPlanInstalled,
    isUserLoggedIn,
  });
  const selectedDate = initializeSelectedDate(
    wixSdkAdapter,
    wixCodeApi,
    !!experiments?.enabled('specs.bookings.isUseUtilsInsteadOfWixSDKEnabled'),
  );

  const serviceVariantsMap = createServicesVariantsMap(servicesVariants);

  return {
    calendarStatus: CalendarStatus.INITIALIZING,
    slotsStatus: SlotsStatus.LOADING,
    servicesInView,
    allCalendarBookableServices,
    selectedDate,
    selectedTimezone: undefined,
    selectedTime: undefined,
    availableSlots: undefined,
    selectableSlotsAtSelectedTime: undefined,
    selectedRange: undefined,
    availableSlotsPerDay: undefined,
    selectedBookingPreferences: [],
    calendarErrors: [],
    rescheduleBookingDetails,
    dialog: undefined,
    filterOptions: {
      [FilterTypes.SERVICE]: [],
      [FilterTypes.LOCATION]: locationFilterOptions,
      [FilterTypes.STAFF_MEMBER]: staffFilterOptions,
    },
    initialErrors,
    purchasedPricingPlans,
    isUserLoggedIn,
    selectedVariantsOptions: [],
    serviceVariantsMap,
  };
}

const createServicesVariantsMap = (
  serviceVariants?: ServiceOptionsAndVariants[],
) => {
  if (!serviceVariants) {
    return {};
  }

  return serviceVariants.reduce<Record<string, ServiceOptionsAndVariants>>(
    (acc, serviceVariant) => {
      acc[serviceVariant.serviceId!] = serviceVariant;
      return acc;
    },
    {},
  );
};

const initializeLocationFilterOptions = ({
  wixSdkAdapter,
  calendarSelections,
  experiments,
  wixCodeApi,
}: {
  wixSdkAdapter: WixOOISDKAdapter;
  calendarSelections?: CalendarSelections;
  experiments?: Experiments;
  wixCodeApi: IWixAPI;
}): string[] => {
  const preSelectedLocations = calendarSelections?.location
    ? [calendarSelections.location]
    : extractQueryParamValues({
        wixCodeApi,
        wixSdkAdapter,
        queryParam: BookingsQueryParams.LOCATION,
        isUseUtilsInsteadOfWixSDKEnabled: !!experiments?.enabled(
          'specs.bookings.isUseUtilsInsteadOfWixSDKEnabled',
        ),
      });

  return preSelectedLocations.filter(
    (location) => location !== ReservedLocationIds.OTHER_LOCATIONS,
  );
};

const initializeStaffMembersFilterOptions = ({
  servicesInView,
  wixSdkAdapter,
  preset,
  staffMembers,
  experiments,
  calendarSelections,
  wixCodeApi,
}: {
  servicesInView: Service[];
  wixCodeApi: IWixAPI;
  wixSdkAdapter: WixOOISDKAdapter;
  preset: Preset;
  staffMembers?: StaffMember[];
  experiments?: Experiments;
  calendarSelections?: CalendarSelections;
}): string[] => {
  const staffFilterOptions: string[] = [];
  const isMultiServiceAppointmentEnabled = experiments?.enabled(
    'specs.bookings.multiServiceAppointment',
  );
  if (isMultiServiceAppointmentEnabled) {
    if (
      calendarSelections?.services &&
      calendarSelections?.services.length === 1
    ) {
      staffFilterOptions.push(calendarSelections.services[0].resources[0]);
      return staffFilterOptions;
    }
    const resourcesIds = extractQueryParamValues({
      wixCodeApi,
      wixSdkAdapter,
      queryParam: BookingsQueryParams.RESOURCE,
      isUseUtilsInsteadOfWixSDKEnabled: !!experiments?.enabled(
        'specs.bookings.isUseUtilsInsteadOfWixSDKEnabled',
      ),
    });
    if (resourcesIds && resourcesIds.includes(DO_NOT_CARE_STAFF)) {
      staffFilterOptions.push(DO_NOT_CARE_STAFF);
    } else {
      staffFilterOptions.push(
        ...initializeStaffMemberFilterOptionsFromQuery({
          wixCodeApi,
          servicesInView,
          wixSdkAdapter,
          preset,
          staffMembers,
          experiments,
        }),
      );
    }
    return staffFilterOptions;
  }
  if (staffMembers) {
    const staffMemberId = staffMembers?.[0]?.id;
    if (staffMemberId) {
      const isStaffMemberConnectedToService = servicesInView.some((service) =>
        _.find(service?.staffMembers, ({ id }) => id === staffMemberId),
      );
      if (isStaffMemberConnectedToService) {
        staffFilterOptions.push(staffMemberId);
      }
    }
  } else {
    staffFilterOptions.push(
      ...initializeStaffMemberFilterOptionsFromQuery({
        wixCodeApi,
        servicesInView,
        wixSdkAdapter,
        preset,
        staffMembers,
        experiments,
      }),
    );
  }

  return staffFilterOptions;
};
const initializeStaffMemberFilterOptionsFromQuery = ({
  servicesInView,
  wixCodeApi,
  wixSdkAdapter,
  preset,
  experiments,
}: {
  wixCodeApi: IWixAPI;
  servicesInView: Service[];
  wixSdkAdapter: WixOOISDKAdapter;
  preset: Preset;
  staffMembers?: StaffMember[];
  experiments?: Experiments;
}): string[] => {
  const staffFilterOptions: string[] = [];
  const resourcesIds = extractQueryParamValues({
    wixCodeApi,
    wixSdkAdapter,
    queryParam: BookingsQueryParams.RESOURCE,
    isUseUtilsInsteadOfWixSDKEnabled: !!experiments?.enabled(
      'specs.bookings.isUseUtilsInsteadOfWixSDKEnabled',
    ),
  });
  if (resourcesIds.length) {
    resourcesIds.forEach((resourceId) => {
      const isStaffMemberConnectedToService = servicesInView.some((service) =>
        _.find(service?.staffMembers, ({ id }) => id === resourceId),
      );
      if (isStaffMemberConnectedToService) {
        staffFilterOptions.push(resourceId);
      }
    });
  }
  return staffFilterOptions;
};
const initializePurchasedPricingPlans = ({
  servicesInView,
  allPurchasedPricingPlans,
  isPricingPlanInstalled,
  isUserLoggedIn,
}: {
  servicesInView: Service[];
  allPurchasedPricingPlans?: Balance[];
  isPricingPlanInstalled: boolean;
  isUserLoggedIn: boolean;
}): Plan[] => {
  let purchasedPricingPlans: Plan[] = [];
  let servicePricingPlans: PricingPlan[] = [];

  if (isPricingPlanInstalled) {
    if (isUserLoggedIn) {
      servicePricingPlans =
        (servicesInView.length &&
          servicesInView[0].payment.pricingPlanInfo?.pricingPlans) ||
        [];
      purchasedPricingPlans = getValidPurchasedPricingPlansForService({
        servicePricingPlans,
        allPurchasedPricingPlans: allPurchasedPricingPlans || [],
      });
    }
  }
  return purchasedPricingPlans;
};

const initializeSelectedDate = (
  wixSdkAdapter: WixOOISDKAdapter,
  wixCodeApi: IWixAPI,
  isUseUtilsInsteadOfWixSDKEnabled: boolean,
): Optional<string> => {
  return isUseUtilsInsteadOfWixSDKEnabled
    ? getUrlQueryParamValue(wixCodeApi, BookingsQueryParams.DATE)
    : wixSdkAdapter.getUrlQueryParamValue(BookingsQueryParams.DATE);
};
